import {
  HARD_MOODS,
  MOODS,
  type Mood,
  type StudentGrade,
  addDays,
  clubsOnDay,
  gradeLevel,
  minutesSince,
  timeOf,
  todayIso,
  useAlarmActions,
  useBirthdays,
  useClubMemberships,
  useClubs,
  useIsOnline,
  useMoodHistory,
  useSetMood,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRightIcon, CaretRightIcon, CheckIcon, SirenIcon } from '../../design/icons';
import { useAge } from '../../design/ui';
import { gradeShort } from '../../lib/labels';
import { useMinute } from '../../lib/now';
import { AbroadCard } from '../AbroadPage';
import { type TaskGroup, useGradeFeed, useNow, useSchoolFeed, useTaskGroups } from './blocks';
import { TodoLinkTo } from './Cards';
import { DoneArt } from './Art';
import {
  type StudentContext,
  dayLong,
  dayMonthShort,
  isoWeekday,
  useShownDay,
  weekdayShort,
} from './model';
import {
  DARK,
  FLAT,
  type Flat,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  MOOD_SHAPE,
  MoodShape,
  PAPER,
  Doodle,
  Shape,
  type ShapeKind,
  grad,
} from './ShapesArt';
import {
  Chip,
  EVENT_FLAT,
  EVENT_GRAD,
  Head,
  OpenArrow,
  PillIcon,
  ShapesDay,
  pillClass,
  ringButton,
  ringSmall,
  useDismiss,
} from './ShapesDay';
import { EventArt, MoodArt, SubjectArt } from './LineArt';
import { type AlarmLike, Marked, type Notice, type Sim, useMoment, useSim } from './Situations';

/*
 * «Форми» — a home of its own (28.10, «совершенно новое, по мудборду, забудь всё прежнее»): warm paper,
 * flat colour, a light grotesk (Onest) set big, a language of plain shapes. The same agreed contents
 * as before — what goes on now and the day, the lessons with the week on top, what to do, the new
 * grades, the school, the mood — and the blocks of the moment that come and go:
 *  — the greeting is no card: a headline on the paper, the face and the counts set into the words
 *    (refs «Creative Leaders Circle», «v.Blend»), the tags as colour pills with stars, the mood a pill;
 *    beside it «Зараз» on lime;
 *  — what to do: flat cards in loud colours, a big shape with the arrow on it (ref. the language app);
 *  — the grades: colour bars, the grade on a white disc (ref. «Exercises 84%»);
 *  — the school: the news as a ruled list, the events as cards with a track of how soon (ref. the
 *    timeline cards);
 *  — an alarm or an announcement to confirm: a band across the top.
 * No black: the ink is a deep pine.
 */

const CARD = 'flex min-w-0 flex-col rounded-[28px] bg-white p-5 md:p-6';

/** The top of the sticky lessons: under the bar, or higher for a list taller than the window. */
function useStickyTop(ref: RefObject<HTMLDivElement | null>) {
  const [top, setTop] = useState(80);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const fit = () => setTop(Math.min(80, window.innerHeight - el.offsetHeight - 16));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [ref]);
  return top;
}

// —— the blocks of the moment: a band across the top ——

function AlarmBand({
  ctx,
  session,
  preview,
  soft,
}: {
  ctx: StudentContext;
  session: AlarmLike;
  preview: boolean;
  soft: boolean;
}) {
  const { t } = useTranslation();
  const actions = useAlarmActions();
  useMinute();
  const drill = session.kind === 'drill';
  const ground = drill ? FLAT.butter : FLAT.orange;
  const ink = drill ? INK : '#FFFFFF';
  const button =
    'inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-white px-4.5 text-[14px] font-medium transition-opacity hover:opacity-90';
  return (
    <div
      role="alert"
      className={`flex min-w-0 flex-col gap-5 rounded-[28px] p-5 md:p-6 ${soft ? 'min-[1700px]:flex-row min-[1700px]:items-center' : 'lg:flex-row lg:items-center'}`}
      style={{
        backgroundColor: ground,
        backgroundImage: soft ? grad(drill ? 'butter' : 'orange') : undefined,
        color: ink,
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span className="relative flex size-16 shrink-0 items-center justify-center">
          {soft ? (
            <span
              className="absolute inset-0 animate-ping rounded-full opacity-30"
              style={{ backgroundColor: drill ? FLAT.orange : '#FFFFFF' }}
            />
          ) : null}
          <Shape
            kind={soft ? 'circle' : 'burst'}
            size={64}
            color={drill ? FLAT.orange : '#FFFFFF'}
            className={`absolute inset-0 ${soft ? '' : 'animate-[spin_12s_linear_infinite]'}`}
          />
          <SirenIcon
            size={26}
            weight="fill"
            aria-hidden
            className="relative"
            color={drill ? '#FFFFFF' : FLAT.orange}
          />
        </span>
        <div className="min-w-0">
          <p className="font-display text-[30px] leading-[34px] font-light tracking-[-0.03em]">
            {t(`alarm.kinds.${session.kind}`)}
          </p>
          <p className="mt-1 text-[15px] leading-5 opacity-85">
            {t('studentHome.shapes.alarmGo')} ·{' '}
            {t('alarm.duration', { minutes: minutesSince(session.started_at) })}
            {!preview && <Marked ctx={ctx} sessionId={session.id} />}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={preview}
          className={soft ? pillClass('light') : button}
          style={soft ? undefined : { color: INK }}
          onClick={() =>
            actions.mark.mutate({
              schoolId: ctx.schoolId,
              sessionId: session.id,
              marks: [{ studentId: ctx.child.memberId, status: 'safe_elsewhere' }],
            })
          }
        >
          {t('alarm.imSafe')}
          {soft && <PillIcon icon={CheckIcon} />}
        </button>
        <Link
          to="/care"
          search={{ open: 'calm' }}
          className={soft ? pillClass('light') : button}
          style={soft ? undefined : { color: INK }}
        >
          {t('care.calm.short')}
          {soft && <PillIcon icon={ArrowUpRightIcon} />}
        </Link>
        <Link
          to="/alarm"
          className={soft ? pillClass('light') : button}
          style={soft ? undefined : { color: INK }}
        >
          {t('shelter.open')}
          {soft && <PillIcon icon={ArrowUpRightIcon} />}
        </Link>
        <Link
          to="/alarm"
          className={
            soft
              ? pillClass('dark')
              : 'inline-flex h-11 shrink-0 items-center gap-2 rounded-full px-5 text-[14px] font-medium text-white'
          }
          style={soft ? undefined : { backgroundColor: DARK }}
        >
          {t('alarm.banner.open')}
          {soft ? (
            <PillIcon icon={ArrowUpRightIcon} />
          ) : (
            <ArrowUpRightIcon size={15} weight="bold" aria-hidden />
          )}
        </Link>
      </div>
    </div>
  );
}

function NoticeBand({
  items,
  onAck,
  soft,
}: {
  items: ReadonlyArray<Notice>;
  onAck: (id: string) => void;
  soft: boolean;
}) {
  const { t } = useTranslation();
  const [first, ...others] = items;
  if (!first) return null;
  return (
    <div
      className="flex min-w-0 flex-col gap-4 rounded-[28px] p-5 sm:flex-row sm:items-center md:p-6"
      style={{
        backgroundColor: FLAT.sky,
        backgroundImage: soft ? grad('sky') : undefined,
        color: INK,
      }}
    >
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-[13px] font-medium">
          {/* «Форми 2»: a label is words, not a pill (the pills here are buttons) */}
          <span
            className={
              soft
                ? 'text-[12px] font-semibold tracking-[0.06em] uppercase'
                : 'inline-flex h-7 items-center gap-1.5 rounded-full px-3'
            }
            style={{
              backgroundColor: soft ? undefined : FLAT.lime,
              color: soft ? GRAD.sky.ink : INK,
            }}
          >
            {!soft && <Shape kind="star" size={12} color={INK} />}
            {t('studentHome.glass2.notice.label')}
          </span>
          <span style={{ color: INK_SOFT }}>
            {[first.author, dayMonthShort(first.publishedAt.slice(0, 10))]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </p>
        <p className="mt-2 font-display text-[24px] leading-[30px] font-light tracking-[-0.02em]">
          {first.title}
        </p>
        {others.length > 0 && (
          <Link
            to="/announcements"
            className="mt-1 inline-flex items-center gap-1 text-[13px] font-medium underline-offset-2 hover:underline"
          >
            {t('studentHome.glass2.notice.more', { count: others.length })}
            <CaretRightIcon size={11} weight="bold" aria-hidden />
          </Link>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Link
          to="/announcements"
          className={
            soft
              ? pillClass('light')
              : 'inline-flex h-11 items-center rounded-full bg-white px-4.5 text-[14px] font-medium'
          }
        >
          {t('studentHome.glass2.notice.open')}
          {soft && <PillIcon icon={ArrowUpRightIcon} />}
        </Link>
        <button
          type="button"
          onClick={() => onAck(first.id)}
          className={
            soft
              ? pillClass('dark')
              : 'inline-flex h-11 items-center rounded-full px-5 text-[14px] font-medium text-white'
          }
          style={soft ? undefined : { backgroundColor: DARK }}
        >
          {t('studentHome.glass2.notice.ack')}
          {soft && <PillIcon icon={CheckIcon} />}
        </button>
      </div>
    </div>
  );
}

export function Moment({
  ctx,
  className = '',
  soft = false,
}: {
  ctx: StudentContext;
  className?: string;
  /** «Форми 2»: gradients, no shapes */
  soft?: boolean;
}) {
  const { session, preview, notices, ack } = useMoment(ctx);
  return (
    <div className={`flex min-w-0 flex-col gap-3 empty:hidden ${className}`}>
      {session && <AlarmBand ctx={ctx} session={session} preview={preview} soft={soft} />}
      {notices.length > 0 && <NoticeBand items={notices} onAck={ack} soft={soft} />}
      <AbroadCard studentId={ctx.child.memberId} self />
    </div>
  );
}

// —— the greeting: a headline on the paper, «Зараз» on lime ——

/** A count set into the words of the headline: a colour pill with a small shape. */
function Inline({ fill, shape, children }: { fill: Flat; shape: ShapeKind; children: ReactNode }) {
  const light = fill === 'orange' || fill === 'cobalt' || fill === 'pine';
  return (
    <span
      className="inline-flex h-[32px] items-center gap-2 rounded-full pr-3.5 pl-2.5 align-[3px] text-[18px] leading-none md:h-[38px] md:pr-4 md:pl-3 md:text-[21px] font-normal tracking-[-0.01em] whitespace-nowrap"
      style={{ backgroundColor: FLAT[fill], color: light ? '#FFFFFF' : INK }}
    >
      <Shape kind={shape} size={15} color={light ? '#FFFFFF' : INK} />
      {children}
    </span>
  );
}

/** The day in one sentence, the counts set into it. */
function DayLine({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const day = useShownDay(ctx);
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([ctx.child.memberId]);
  if (day.pending) return <p className="h-[76px]" aria-busy />;
  const held = day.lessons.filter((l) => l.status !== 'cancelled');
  if (!day.date || held.length === 0)
    return (
      <p className="text-[26px] leading-[40px] font-light" style={{ color: INK_SOFT }}>
        {t('studentHome.glass.now.noLessons')}
      </p>
    );
  const tests = held.filter((l) => l.isTest).length;
  const club = clubsOnDay(
    clubs.data ?? [],
    memberships.data ?? [],
    ctx.child.memberId,
    isoWeekday(day.date),
  )[0];
  const when = day.isToday
    ? t('studentHome.shapes.today')
    : day.date === addDays(todayIso(), 1)
      ? t('studentHome.shapes.tomorrow')
      : dayLong(day.date);
  return (
    <p
      className="max-w-[640px] font-display text-[22px] leading-[38px] font-light tracking-[-0.02em] md:text-[26px] md:leading-[44px]"
      style={{ color: INK_SOFT }}
    >
      <span style={{ color: INK }}>{when}</span> —{' '}
      <Inline fill="lavender" shape="flower">
        {t('student.lessonsCount', { count: held.length })}
      </Inline>{' '}
      {t('studentHome.shapes.fromTo', {
        from: timeOf((held[0] as (typeof held)[number]).startsAt),
        to: timeOf((held.at(-1) as (typeof held)[number]).endsAt),
      })}
      {tests > 0 && (
        <>
          ,{' '}
          <Inline fill="orange" shape="burst">
            {t('studentHome.shapes.tests', { count: tests })}
          </Inline>
        </>
      )}
      {club && (
        <>
          , {t('studentHome.shapes.then')}{' '}
          <Inline fill="mint" shape="plus">
            {club.club.title}
          </Inline>{' '}
          {t('studentHome.shapes.at', { time: club.slot.startsAt.slice(0, 5) })}
        </>
      )}
      .
    </p>
  );
}

const TAG =
  'inline-flex h-9 shrink-0 items-center gap-2 rounded-full px-3.5 text-[14px] font-medium';
const CLUB_FILLS: ReadonlyArray<Flat> = ['lavender', 'mint', 'butter', 'pink'];

/** The mood of today: saved or just picked, how to pick it, the last week of it. */
export function useMood(ctx: StudentContext) {
  const history = useMoodHistory(ctx.child.memberId, 7);
  const setMood = useSetMood();
  const online = useIsOnline();
  const today = todayIso();
  const [picked, setPicked] = useState<Mood>();
  const saved = history.data?.find((d) => d.day === today)?.mood;
  const mood = picked ?? saved;
  const byDay = new Map((history.data ?? []).map((d) => [d.day, d.mood]));
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));
  const choose = (m: Mood) => {
    setPicked(m);
    setMood.mutate({ schoolId: ctx.schoolId, mood: m });
  };
  return { mood, choose, byDay, days, today, online };
}

export type MoodState = ReturnType<typeof useMood>;

/** What opens to pick the mood: the faces, a word back (help when it is hard), the week, «Турбота». */
export function MoodBody({ state, soft = false }: { state: MoodState; soft?: boolean }) {
  const { t } = useTranslation();
  const { mood, choose, byDay, days, today, online } = state;
  return (
    <>
      <p
        className="font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
        style={{ color: INK }}
      >
        {t('studentHome.glass2.mood.title')}
      </p>
      <div className="mt-4 grid grid-cols-6 gap-1">
        {MOODS.map((m, i) => (
          <button
            key={m}
            type="button"
            aria-pressed={mood === m}
            aria-label={t(`care.moods.${m}`)}
            title={t(`care.moods.${m}`)}
            onClick={() => choose(m)}
            className="motion-pop flex flex-col items-center gap-1.5 rounded-[18px] py-2 transition-[background-color,transform] duration-300 ease-spring hover:-translate-y-1"
            style={
              {
                '--delay': `${i * 0.05}s`,
                backgroundColor: mood === m ? PAPER : undefined,
              } as CSSProperties
            }
          >
            {soft ? <MoodArt mood={m} size={40} /> : <MoodShape mood={m} size={40} />}
            <span className="text-[10.5px] leading-3 font-medium" style={{ color: INK_SOFT }}>
              {t(`care.moods.${m}`)}
            </span>
          </button>
        ))}
      </div>
      {mood && (
        <div
          className="mt-3 rounded-[18px] px-3.5 py-3 text-[13px] leading-[18px]"
          style={{ backgroundColor: PAPER, color: INK }}
        >
          {!online
            ? t('care.mood.queued')
            : HARD_MOODS.has(mood)
              ? t('care.mood.hard')
              : t('care.mood.good')}
          {HARD_MOODS.has(mood) && (
            <span className="mt-2.5 flex flex-wrap gap-2">
              <Link
                to="/care"
                search={{ open: 'calm' }}
                className="inline-flex h-9 items-center rounded-full bg-white px-3.5 text-[13px] font-medium ring-1 ring-[#E4E0D7] ring-inset"
              >
                {t('care.actions.calm')}
              </Link>
              <Link
                to="/care"
                className="inline-flex h-9 items-center rounded-full px-3.5 text-[13px] font-medium text-white"
                style={{ backgroundColor: DARK }}
              >
                {t('care.actions.write')}
              </Link>
            </span>
          )}
        </div>
      )}
      <p className="mt-5 mb-2 text-[12px] font-medium" style={{ color: INK_FAINT }}>
        {t('studentHome.glass2.mood.week')}
      </p>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const m = byDay.get(d) ?? (d === today ? mood : undefined);
          return (
            <span key={d} className="flex flex-col items-center gap-1">
              {m ? (
                soft ? (
                  <MoodArt mood={m} size={26} />
                ) : (
                  <MoodShape mood={m} size={26} />
                )
              ) : (
                <span className="size-[26px] rounded-full" style={{ backgroundColor: PAPER }} />
              )}
              <span
                className="text-[11px]"
                style={{
                  color: d === today ? INK : INK_FAINT,
                  fontWeight: d === today ? 600 : 400,
                }}
              >
                {weekdayShort(d)}
              </span>
            </span>
          );
        })}
      </div>
      <Link
        to="/care"
        className="mt-4 flex items-center justify-between rounded-full px-4 py-2.5 text-[13px] font-medium"
        style={{ backgroundColor: PAPER, color: INK }}
      >
        {t('studentHome.glass2.mood.more')}
        <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
      </Link>
    </>
  );
}

/** «Як ти?» as a pill: the shapes with faces pop up to choose from, the week under them. */
export function MoodPill({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const state = useMood(ctx);
  const { mood } = state;
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`${TAG} pl-1.5 ring-1 ring-inset transition-colors hover:bg-white`}
        style={{
          color: INK,
          backgroundColor: mood ? FLAT[MOOD_SHAPE[mood].fill] : 'transparent',
          boxShadow: mood ? undefined : `inset 0 0 0 1px ${LINE}`,
        }}
      >
        {mood ? (
          <MoodShape mood={mood} size={26} />
        ) : (
          <span className="flex size-[26px] items-center justify-center">
            <Shape kind="star" size={16} color={FLAT.orange} />
          </span>
        )}
        {mood ? t(`care.moods.${mood}`) : t('studentHome.glass2.mood.ask')}
      </button>
      {open && (
        <div
          className="absolute top-full left-0 z-40 mt-2 w-[min(360px,calc(100vw-48px))] rounded-[28px] bg-white p-5"
          style={{ boxShadow: `inset 0 0 0 1px ${LINE}, 0 24px 48px -28px rgb(22 55 45 / 0.4)` }}
        >
          <MoodBody state={state} />
        </div>
      )}
    </div>
  );
}

/** The tags under the day: the class, the clubs as colour pills with stars, a birthday near, the mood. */
function Tags({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([ctx.child.memberId]);
  const birthdays = useBirthdays(ctx.schoolId, 14);
  const today = todayIso();
  const mine = new Set(
    (memberships.data ?? [])
      .filter((m) => m.studentId === ctx.child.memberId && m.status === 'active')
      .map((m) => m.clubId),
  );
  const myClubs = (clubs.data ?? []).filter((c) => mine.has(c.id)).slice(0, 3);
  const birthday = (birthdays.data ?? []).find((b) => b.memberId === ctx.child.memberId);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {ctx.child.class && (
        <span className={TAG} style={{ color: INK, boxShadow: `inset 0 0 0 1px ${LINE}` }}>
          {t('studentHome.glass2.classOf', { label: ctx.child.class.label })}
        </span>
      )}
      {myClubs.map((c, i) => (
        <Link
          key={c.id}
          to="/clubs"
          className={TAG}
          style={{ backgroundColor: FLAT[CLUB_FILLS[i % CLUB_FILLS.length] as Flat], color: INK }}
        >
          <Shape kind={i % 2 === 0 ? 'star' : 'burst'} size={13} color={INK} />
          {c.title}
        </Link>
      ))}
      {birthday && (
        <span className={TAG} style={{ backgroundColor: FLAT.pink, color: INK }}>
          <Shape kind="flower" size={14} color={INK} />
          {birthday.onDate === today
            ? t('studentHome.glass2.birthdayToday')
            : t('studentHome.glass2.birthday', { date: dayMonthShort(birthday.onDate) })}
        </span>
      )}
      <MoodPill ctx={ctx} />
    </div>
  );
}

/** «Зараз» on lime: what goes on with its time left, then the next lesson on a white row. */
function NowCard({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const state = useNow(ctx);
  if (!state.ready)
    return (
      <div className="h-[260px] rounded-[28px]" style={{ backgroundColor: FLAT.lime }} aria-busy />
    );
  const { current, next } = state;
  const progress =
    current.progress === undefined ? undefined : Math.min(1, Math.max(0, current.progress));
  return (
    <div
      className="relative flex min-w-0 flex-col rounded-[28px] p-5 md:p-6"
      style={{ backgroundColor: FLAT.lime, color: INK }}
    >
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-medium" style={{ color: INK_SOFT }}>
          {t('studentHome.glass.now.now')}
        </span>
        {current.aside && (
          <span className="ml-auto">
            <Chip>
              {current.live && (
                <span
                  className="size-1.5 animate-pulse rounded-full"
                  style={{ backgroundColor: FLAT.orange }}
                />
              )}
              {current.aside}
            </Chip>
          </span>
        )}
      </div>
      <p className="mt-1 truncate font-display text-[40px] leading-[46px] font-light tracking-[-0.035em]">
        {current.title}
      </p>
      {current.sub && (
        <p className="truncate text-[13.5px]" style={{ color: INK_SOFT }}>
          {current.sub}
        </p>
      )}
      {progress !== undefined && (
        <span className="relative mt-4 block h-[3px] rounded-full bg-white/70">
          <span
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${progress * 100}%`, backgroundColor: INK }}
          />
          <span
            className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
            style={{ left: `${progress * 100}%`, boxShadow: `inset 0 0 0 3px ${INK}` }}
          />
        </span>
      )}
      {next ? (
        <div className="mt-5 flex items-center gap-3 rounded-[20px] bg-white p-3 pl-4">
          <span className="flex shrink-0 flex-col leading-tight">
            <span className="text-[11.5px] font-medium" style={{ color: INK_FAINT }}>
              {next.label}
            </span>
            <span className="font-display text-[24px] font-light tracking-[-0.02em]">
              {timeOf(next.lesson.startsAt)}
            </span>
          </span>
          <span className="h-9 w-px shrink-0" style={{ backgroundColor: LINE }} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[16px] leading-[22px] font-medium">
              {next.lesson.subject}
            </span>
            <span
              className="block truncate text-[12.5px] leading-[18px]"
              style={{ color: INK_SOFT }}
            >
              {next.extra ?? next.lesson.room ?? ''}
            </span>
          </span>
          <OpenArrow to="/schedule" label={t('studentHome.glass.week.all')} />
        </div>
      ) : (
        <p className="mt-5 rounded-[20px] bg-white p-4 text-[14px]" style={{ color: INK_SOFT }}>
          {state.nothing}
        </p>
      )}
      {state.join && (
        <Link
          to="/lesson/$lessonId"
          params={{ lessonId: state.join.id }}
          className="mt-3 inline-flex h-11 items-center justify-center rounded-full text-[14px] font-medium text-white"
          style={{ backgroundColor: DARK }}
        >
          {t('student.lessons.joinLesson')}
        </Link>
      )}
    </div>
  );
}

function Hero({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const age = useAge();
  const initials = `${ctx.child.firstName[0] ?? ''}${ctx.child.lastName[0] ?? ''}`.toUpperCase();
  return (
    <section
      className={`relative grid min-w-0 grid-cols-1 items-end gap-6 pt-2 pb-2 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)] lg:gap-10 ${className}`}
    >
      <div className="relative flex min-w-0 flex-col gap-5">
        <p className="flex items-center gap-2 text-[14px] font-medium" style={{ color: INK_SOFT }}>
          <Shape kind="star" size={13} color={FLAT.orange} />
          {dayLong(todayIso())}
        </p>
        <h1
          className={`font-display font-light tracking-[-0.045em] ${age === 'junior' ? 'text-[60px] leading-[64px] md:text-[80px] md:leading-[84px]' : 'text-[52px] leading-[58px] md:text-[72px] md:leading-[78px]'}`}
          style={{ color: INK }}
        >
          {t('studentHome.shapes.hi')}
          <span
            className="mx-3 inline-flex size-[52px] -translate-y-1 items-center justify-center rounded-full align-middle text-[19px] font-medium tracking-normal md:size-[64px] md:text-[22px]"
            style={{ backgroundColor: FLAT.lime }}
            aria-hidden
          >
            {initials}
          </span>
          {ctx.child.firstName}.
        </h1>
        <DayLine ctx={ctx} />
        <Tags ctx={ctx} />
        <Doodle className="absolute -right-12 bottom-[30%] hidden w-[150px] rotate-[-8deg] xl:block" />
      </div>
      <NowCard ctx={ctx} />
    </section>
  );
}

// —— what to do: loud flat cards, a big shape with the arrow on it ——

const GROUP_LOOK: Record<string, { fill: Flat; light: boolean; shape: ShapeKind; mark: Flat }> = {
  overdue: { fill: 'orange', light: true, shape: 'burst', mark: 'lime' },
  soon: { fill: 'cobalt', light: true, shape: 'circle', mark: 'lime' },
  tests: { fill: 'lavender', light: false, shape: 'diamond', mark: 'orange' },
  rework: { fill: 'butter', light: false, shape: 'square', mark: 'cobalt' },
  ack: { fill: 'pink', light: false, shape: 'star', mark: 'cobalt' },
  catchUp: { fill: 'mint', light: false, shape: 'triangle', mark: 'cobalt' },
  done: { fill: 'lime', light: false, shape: 'flower', mark: 'mint' },
};

function TaskCard({
  group,
  wide = false,
  className = '',
}: {
  group: TaskGroup;
  wide?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const look = GROUP_LOOK[group.key] ?? (GROUP_LOOK.done as (typeof GROUP_LOOK)[string]);
  const ink = look.light ? '#FFFFFF' : INK;
  const shown = group.rows.slice(0, wide ? 3 : 2);
  const hidden = group.rows.length - shown.length;
  return (
    <div
      className={`relative flex min-w-0 flex-col gap-4 overflow-hidden rounded-[24px] p-4 md:p-5 ${wide ? 'sm:grid sm:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] sm:items-center sm:gap-5' : ''} ${className}`}
      style={{ backgroundColor: FLAT[look.fill], color: ink }}
    >
      <div className="flex items-start gap-3">
        <TodoLinkTo link={group.link} className="min-w-0 flex-1">
          <span className="block text-[14px] leading-5 font-medium opacity-85">{group.title}</span>
          {group.text ? (
            <span className="mt-2 block text-[15px] leading-[22px]">{group.text}</span>
          ) : (
            <span className="block font-display text-[64px] leading-[64px] font-light tracking-[-0.05em]">
              {group.count}
            </span>
          )}
        </TodoLinkTo>
        <span className="relative flex size-[84px] shrink-0 items-center justify-center">
          <Shape kind={look.shape} size={84} color={FLAT[look.mark]} className="absolute inset-0" />
          <TodoLinkTo
            link={group.link}
            label={t('studentHome.cards.open')}
            className="relative flex size-10 items-center justify-center rounded-full text-white transition-transform duration-500 ease-spring hover:rotate-45"
          >
            <span className="absolute inset-0 rounded-full" style={{ backgroundColor: INK }} />
            <ArrowUpRightIcon size={17} weight="bold" aria-hidden className="relative" />
          </TodoLinkTo>
        </span>
      </div>
      {group.key !== 'done' && (
        <div className="flex min-w-0 flex-col gap-1.5">
          {shown.map((r) => (
            <TodoLinkTo
              key={r.key}
              link={r.link}
              className="group flex min-h-[52px] items-center gap-3 rounded-[16px] bg-white px-3.5 py-2 transition-transform duration-300 ease-spring hover:-translate-y-0.5"
            >
              <span className="min-w-0 flex-1">
                <span
                  className="block truncate text-[14px] leading-5 font-medium"
                  style={{ color: INK }}
                >
                  {r.title}
                </span>
                <span className="block truncate text-[12px] leading-4" style={{ color: INK_SOFT }}>
                  {r.sub}
                </span>
              </span>
              <CaretRightIcon
                size={12}
                weight="bold"
                aria-hidden
                color={INK}
                className="shrink-0 transition-transform group-hover:translate-x-0.5"
              />
            </TodoLinkTo>
          ))}
          {hidden > 0 && (
            <TodoLinkTo
              link={group.link}
              className="inline-flex items-center gap-1 self-start px-1 pt-1 text-[13px] font-medium underline-offset-2 hover:underline"
            >
              {t('studentHome.glass.board.more', { count: hidden })}
              <CaretRightIcon size={11} weight="bold" aria-hidden />
            </TodoLinkTo>
          )}
        </div>
      )}
    </div>
  );
}

function Todo({ ctx, sim }: { ctx: StudentContext; sim: ReadonlySet<Sim> }) {
  const { t } = useTranslation();
  const tasks = useTaskGroups(ctx);
  // what to confirm stands on top in a band
  let groups = tasks.groups.filter((g) => g.key !== 'ack');
  if (sim.has('notasks')) groups = [];
  if (sim.has('onetask')) groups = groups.slice(0, 1);
  if (!tasks.pending && groups.length === 0)
    groups = [
      {
        key: 'done',
        tone: 'green',
        art: DoneArt,
        title: t('studentHome.glass.board.done'),
        count: 0,
        rows: [],
        link: { to: '/homework' },
        text: t('studentHome.glass.board.doneText'),
      },
    ];
  const total = groups.filter((g) => g.key !== 'done').reduce((n, g) => n + g.count, 0);
  const odd = groups.length % 2 === 1;
  const keys = groups.map((g) => g.key).join(',');
  const line = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (line.current && keys) line.current.scrollLeft = 0;
  }, [keys]);
  return (
    <section className={CARD}>
      <Head title={t('studentHome.glass.todo')} count={total}>
        <OpenArrow to="/homework" label={t('studentHome.glass.all')} />
      </Head>
      {tasks.pending && groups.length === 0 ? (
        <div
          className="h-[220px] animate-pulse rounded-[24px]"
          style={{ backgroundColor: PAPER }}
          aria-busy
        />
      ) : (
        <div
          ref={line}
          className={`no-scrollbar grid snap-x snap-mandatory grid-flow-col gap-3 overflow-x-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible ${groups.length === 1 ? 'auto-cols-[100%]' : 'auto-cols-[86%] sm:auto-cols-auto'}`}
        >
          {groups.map((g, i) => (
            <TaskCard
              key={g.key}
              group={g}
              wide={odd && i === 0}
              className={`snap-start ${odd && i === 0 ? 'sm:col-span-2' : ''}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// —— the new grades: colour bars, the grade on a white disc ——

const LEVEL_FLAT: ReadonlyArray<Flat> = ['orange', 'butter', 'sky', 'lime'];
const LEVEL_GRAD: ReadonlyArray<Grad> = ['coral', 'butter', 'sky', 'lime'];

/**
 * «Форми 2»: the new grades as small tiles, side by side (his edits of 28.10: big tiles — «это всего лишь
 * оценки»; a bare list — «бедненько»; wide cards — «оценки маленькие штуки, зачем широкие»): the grade big,
 * in the interface's font (by hand until 29.10, his word), the subject's picture, the subject and the day,
 * on the level's gradient. As many in a row as fit, never stretched past a small tile — one grade is one
 * small tile. Eight places: past that the last is «+N · Усі оцінки ↗». «Нова» only as a mark, and only when
 * some are already seen.
 */
function GradeList({
  grades,
  unread,
}: {
  grades: ReadonlyArray<StudentGrade>;
  unread: ReadonlySet<string | undefined>;
}) {
  const { t } = useTranslation();
  const places = 8;
  const shown = grades.length > places ? grades.slice(0, places - 1) : grades;
  const more = grades.length - shown.length;
  const mixed = grades.some((g) => !unread.has(g.id));
  const tile =
    'group flex h-full min-h-[128px] min-w-0 flex-col rounded-[22px] p-3.5 transition-transform duration-300 ease-spring hover:-translate-y-0.5';
  return (
    <section className={CARD}>
      <Head title={t('studentHome.glass.newGrades')} count={grades.length}>
        <OpenArrow to="/grades" label={t('studentHome.glass.allGrades')} soft />
      </Head>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(128px,1fr))] gap-2">
        {shown.map((g) => {
          const thematic = g.kind === 'thematic' || g.kind === 'semester' || g.kind === 'year';
          const tone: Grad = thematic
            ? 'lavender'
            : g.value
              ? (LEVEL_GRAD[gradeLevel(g.value)] ?? 'stone')
              : 'stone';
          return (
            <li key={g.id} className="min-w-0">
              <Link
                to="/grades"
                className={tile}
                style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
              >
                <span className="flex items-start justify-between gap-1">
                  <span
                    className="-mt-1 font-display text-[38px] leading-[44px] font-light tracking-[-0.03em] tabular-nums"
                    style={{ color: INK }}
                  >
                    {gradeShort(t, g.value, g.special)}
                  </span>
                  {mixed && unread.has(g.id) ? (
                    <Chip soft tone="orange">
                      {t('studentHome.glass.fresh')}
                    </Chip>
                  ) : (
                    <SubjectArt icon={subjectLook(g.subject).icon} tone={tone} size={32} />
                  )}
                </span>
                <span className="mt-auto line-clamp-2 pt-2 text-[14px] leading-[18px] font-medium">
                  {g.subject}
                </span>
                <span
                  className="mt-0.5 flex flex-col text-[11.5px] leading-4"
                  style={{ color: INK_SOFT }}
                  title={g.comment ?? undefined}
                >
                  <span className="truncate">
                    {g.title ?? g.topic ?? t(`journal.kinds.${g.kind}`)}
                  </span>
                  <span>{dayMonthShort(g.date)}</span>
                </span>
              </Link>
            </li>
          );
        })}
        {more > 0 && (
          <li className="min-w-0">
            <Link to="/grades" className={tile} style={{ backgroundColor: PAPER, color: INK }}>
              <span className="flex items-start justify-between gap-1">
                <span className="font-display text-[30px] leading-[40px] font-light tracking-[-0.03em]">
                  +{more}
                </span>
                <span
                  className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
                >
                  <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
                </span>
              </span>
              <span className="mt-auto pt-2 text-[14px] leading-[18px] font-medium">
                {t('studentHome.glass.allGrades')}
              </span>
            </Link>
          </li>
        )}
      </ul>
    </section>
  );
}

export function Grades({
  ctx,
  sim,
  soft = false,
}: {
  ctx: StudentContext;
  sim: ReadonlySet<Sim>;
  /** «Форми 2»: a ruled list, the grades as marks */
  soft?: boolean;
}) {
  const { t } = useTranslation();
  const { grades, unread } = useGradeFeed(ctx);
  const week = addDays(todayIso(), -7);
  let fresh = grades.filter((g) => unread.has(g.id) || g.date > week);
  if (sim.has('nogrades')) fresh = [];
  if (sim.has('onegrade')) fresh = fresh.slice(0, 1);
  if (fresh.length === 0) return null;
  if (soft) return <GradeList grades={fresh} unread={unread} />;
  const shown = fresh.slice(0, 6);
  return (
    <section className={CARD}>
      <Head title={t('studentHome.glass.newGrades')} count={fresh.length}>
        <OpenArrow to="/grades" label={t('studentHome.glass.allGrades')} />
      </Head>
      <ul className="flex flex-wrap gap-2">
        {shown.map((g) => {
          const thematic = g.kind === 'thematic' || g.kind === 'semester' || g.kind === 'year';
          const fill: Flat = thematic
            ? 'lavender'
            : g.value
              ? (LEVEL_FLAT[gradeLevel(g.value)] ?? 'stone')
              : 'stone';
          const light = fill === 'orange';
          return (
            <li key={g.id} className="min-w-0 grow basis-[270px]">
              <Link
                to="/grades"
                className="flex h-[64px] min-w-0 items-center gap-3 rounded-full py-1.5 pr-1.5 pl-5 transition-transform duration-300 ease-spring hover:-translate-y-0.5"
                style={{ backgroundColor: FLAT[fill], color: light ? '#FFFFFF' : INK }}
              >
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-[17px] leading-[22px]">{g.subject}</span>
                    {unread.has(g.id) && (
                      <Shape kind="star" size={12} color={light ? '#FFFFFF' : INK} />
                    )}
                  </span>
                  <span className="block truncate text-[12px] leading-4 opacity-75">
                    {g.comment
                      ? `«${g.comment}»`
                      : (g.title ?? g.topic ?? t(`journal.kinds.${g.kind}`))}
                    {' · '}
                    {dayMonthShort(g.date)}
                  </span>
                </span>
                <span
                  className="flex size-[52px] shrink-0 items-center justify-center rounded-full bg-white font-display text-[24px] font-light tracking-[-0.02em]"
                  style={{ color: INK }}
                >
                  {gradeShort(t, g.value, g.special)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {fresh.length > shown.length && (
        <Link
          to="/grades"
          className="mt-3 inline-flex items-center gap-1 self-start text-[13px] font-medium underline-offset-2 hover:underline"
          style={{ color: INK }}
        >
          {t('studentHome.glass2.grades.more', { count: fresh.length - shown.length })}
          <CaretRightIcon size={11} weight="bold" aria-hidden />
        </Link>
      )}
    </section>
  );
}

// —— the school: a ruled list of news, the events with a track of how soon ——

/** Where an event stands on the track of two weeks: today, this week, next week, later. */
const stepOf = (days: number) => (days <= 1 ? 0 : days <= 6 ? 1 : days <= 10 ? 2 : 3);

export function School({
  ctx,
  sim,
  soft = false,
}: {
  ctx: StudentContext;
  sim: ReadonlySet<Sim>;
  /** «Форми 2»: gradient cards, the date and the place as words, no stars */
  soft?: boolean;
}) {
  const { t } = useTranslation();
  const feed = useSchoolFeed(ctx);
  const today = todayIso();
  const news = sim.has('noschool') ? [] : feed.news;
  const coming = sim.has('noschool') ? [] : feed.coming;
  if (news.length === 0 && coming.length === 0) return null;
  return (
    <section className={CARD}>
      <Head title={t('studentHome.glass.school.title')}>
        <OpenArrow to="/events" label={t('studentHome.glass.school.all')} soft={soft} />
      </Head>
      {news.length > 0 && (
        <ul
          className={`flex flex-col ${coming.length > 0 ? 'mb-4' : ''}`}
          style={{ borderTop: `1px solid ${LINE}` }}
        >
          {news.map((a) => (
            <li key={a.id} style={{ borderBottom: `1px solid ${LINE}` }}>
              <Link to="/announcements" className="group flex items-center gap-4 py-3.5">
                {!soft && <Shape kind="star" size={14} color={FLAT.orange} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[17px] leading-6" style={{ color: INK }}>
                    {a.title}
                  </span>
                  <span className="block truncate text-[12.5px]" style={{ color: INK_SOFT }}>
                    {[a.author, feed.ago(a.publishedAt)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-full ring-1 ring-[#E4E0D7] ring-inset transition-colors group-hover:bg-[#F5F3EF]"
                  style={{ color: INK }}
                >
                  <ArrowUpRightIcon size={15} weight="bold" aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {coming.length > 0 && (
        <div className="flex flex-wrap gap-2.5">
          {coming.map((e) => {
            const fill = EVENT_FLAT[e.kind];
            const softFill = EVENT_GRAD[e.kind];
            const light = !soft && fill === 'cobalt';
            const days = Math.max(
              0,
              Math.round((Date.parse(e.startsOn) - Date.parse(today)) / 86_400_000),
            );
            const step = stepOf(days);
            return (
              <Link
                key={e.id}
                to="/events"
                className={`flex min-h-[176px] min-w-0 grow basis-[230px] flex-col rounded-[24px] p-4 transition-transform duration-300 ease-spring hover:-translate-y-0.5 ${soft ? 'relative isolate overflow-hidden' : ''}`}
                style={
                  soft
                    ? { backgroundImage: grad(softFill), color: GRAD[softFill].ink }
                    : { backgroundColor: FLAT[fill], color: light ? '#FFFFFF' : INK }
                }
              >
                {soft ? (
                  // «Форми 2»: how soon, as words (the track of «Форми» read as nothing); its picture big
                  // and quiet behind the words (his «крупней, на фоне, не бросается в глаза», 28.10)
                  <>
                    <EventArt
                      kind={e.kind}
                      tone={softFill}
                      size={150}
                      ghost
                      className="pointer-events-none absolute -top-4 -right-5 -z-10"
                    />
                    <span
                      className="self-start rounded-[7px] bg-white/75 px-2 py-1 text-[12px] leading-none font-medium whitespace-nowrap"
                      style={{ color: INK }}
                    >
                      {feed.until(e.startsOn, e.endsOn)}
                    </span>
                  </>
                ) : (
                  <>
                    {/* the track: today — this week — next week — later; the event's dot filled */}
                    <span className="relative flex items-center justify-between">
                      <span
                        className="absolute inset-x-1.5 top-1/2 h-px opacity-40"
                        style={{ backgroundColor: light ? '#FFFFFF' : INK }}
                      />
                      {[0, 1, 2, 3].map((i) => (
                        <span
                          key={i}
                          className="relative size-3 rounded-full"
                          style={{
                            backgroundColor:
                              i === step
                                ? soft
                                  ? GRAD[softFill].ink
                                  : light
                                    ? FLAT.lime
                                    : FLAT.orange
                                : soft
                                  ? '#FFFFFF'
                                  : FLAT[fill],
                            boxShadow:
                              i === step
                                ? undefined
                                : `inset 0 0 0 1.5px ${light ? 'rgb(255 255 255 / 0.6)' : 'rgb(22 55 45 / 0.35)'}`,
                          }}
                        />
                      ))}
                    </span>
                    {/* the words under their dot: from the left for the near ones, from the right for the far */}
                    <span
                      className="mt-1.5 text-[11.5px] font-medium whitespace-nowrap"
                      style={
                        step <= 1
                          ? { paddingLeft: `${step * 33}%` }
                          : { paddingRight: `${(3 - step) * 33}%`, textAlign: 'right' }
                      }
                    >
                      {feed.until(e.startsOn, e.endsOn)}
                    </span>
                  </>
                )}
                <span className="mt-auto pt-4 font-display text-[21px] leading-[26px] font-light tracking-[-0.02em]">
                  {e.title}
                </span>
                {soft ? (
                  <span className="mt-2 truncate text-[12.5px] opacity-80">
                    {[
                      `${weekdayShort(e.startsOn)}, ${dayMonthShort(e.startsOn)}`,
                      e.atTime,
                      e.place,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                ) : (
                  <span className="mt-3 flex flex-wrap gap-1.5">
                    <span
                      className="inline-flex h-7 items-center rounded-full px-3 text-[12px]"
                      style={{
                        boxShadow: `inset 0 0 0 1px ${light ? 'rgb(255 255 255 / 0.6)' : 'rgb(22 55 45 / 0.3)'}`,
                      }}
                    >
                      {weekdayShort(e.startsOn)}, {dayMonthShort(e.startsOn)}
                      {e.atTime && ` · ${e.atTime}`}
                    </span>
                    {e.place && (
                      <span
                        className="inline-flex h-7 max-w-full items-center truncate rounded-full px-3 text-[12px]"
                        style={{
                          boxShadow: `inset 0 0 0 1px ${light ? 'rgb(255 255 255 / 0.6)' : 'rgb(22 55 45 / 0.3)'}`,
                        }}
                      >
                        {e.place}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// —— the page ——

export function ShapesHome({ ctx }: { ctx: StudentContext }) {
  const sim = useSim();
  const day = useRef<HTMLDivElement>(null);
  const top = useStickyTop(day);
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <Moment ctx={ctx} className="xl:col-span-12" />
      <Hero ctx={ctx} className="xl:col-span-12" />
      <div ref={day} className="min-w-0 xl:sticky xl:col-span-5 xl:self-start" style={{ top }}>
        <ShapesDay ctx={ctx} />
      </div>
      {/* beside the lessons: a plain stack — a block of the moment comes or goes, the others close up */}
      <div className="flex min-w-0 flex-col gap-4 xl:col-span-7">
        <Todo ctx={ctx} sim={sim} />
        <Grades ctx={ctx} sim={sim} />
        <School ctx={ctx} sim={sim} />
      </div>
    </div>
  );
}
